import _ from "lodash";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useStorageState} from "react-storage-hooks";
import {handleApiException, useHttp} from "../../../services/HttpService";
import {getMessstelleSucheEndpoint} from "../../../services/Messstellenservice";
import "../../../styles/components/messstelleArbeitsListe.css";
import {useForm} from "../../../utils/FormUtils";
import {changeFieldValueIfPresent} from "../../../utils/ObjectUtils";
import {toBase64QueryParam} from "../../../utils/URLUtils";
import {getDefaultPageInfo, toQueryParams} from "../../../utils/PageInfoUtils";
import usePersonalisationAndPagination from "../usePersonalisationAndPagination";
import MessstelleArbeitsListeSuche from "./MessstelleArbeitsListeSuche";
import MessstelleArbeitsListeTable from "./MessstelleArbeitsListeTable";

const initialSearchParameters = {
    messstellenstatus: {
        id: "IN_BETRIEB",
        value: "in Betrieb",
    },
    messstellentyp: {
        id: "NR_12",
        value: "Endkontrollmessstelle"
    },
    pageInfo: getDefaultPageInfo("messstellenId")
};

function MessstelleArbeitsListe({
                                    history,
                                    sharedFilter,
                                    onErweiterteSucheOpenChanged,
                                    sharedFormFactory,
                                    regionalstelleOptionen,
                                    raeumlichesKriteriumOptionen,
                                    behoerdeOptionen,
                                    resetSharedFilter,
                                }) {
    const id = "MessstelleArbeitsliste";
    const attr = {
        verwaltungseinheit: "verwaltungseinheit",
        wasserkoerper: "wasserkoerper",
        anlagenBezeichnung: "anlagenBezeichnung",
        abwasserbeseitigungspflichtiger: "abwasserbeseitigungspflichtiger",

        bezeichnung: "bezeichnung",
        messstellenNr: "messstellenNr",
        messstellenstatus: "messstellenstatus",
        messstellentyp: "messstellentyp",

        regionalstelle: "regionalstelle",
        aktenzeichen: "aktenzeichen",
        bescheidsdatum: "bescheidsdatum",
        behoerde: "behoerde",

        gueltigVon: "gueltigVon",
        gueltigBis: "gueltigBis",
    };

    const [selected, setSelected] = useStorageState(sessionStorage, "eab-selected-messstellen", []);
    const [storedFilter, setStoredFilter] = useStorageState(sessionStorage, "eab-messstellen-filter", initialSearchParameters);

    const {values: filter, setValues: setFilter, formFactory} = useForm({initialValues: storedFilter});

    const createQuery = useCallback((filterObject) => {
        let f = _.cloneDeep(filterObject);
        if (!f.extendedSearchOpen) {
            delete f.raeumlich;
            delete f.gueltigVon;
            delete f.gueltigBis;
            delete f.regionalstelle;
            delete f.aktenzeichen;
            delete f.bescheidsdatum;
            delete f.behoerde;
        }

        changeFieldValueIfPresent(f, attr.messstellentyp, "id");
        changeFieldValueIfPresent(f, attr.messstellenstatus, "id");
        changeFieldValueIfPresent(f, attr.regionalstelle, "id");
        changeFieldValueIfPresent(f, attr.behoerde, "id");
        changeFieldValueIfPresent(f, attr.abwasserbeseitigungspflichtiger, "id");

        if (f.raeumlich) {
            f[attr.verwaltungseinheit] = f["raeumlich"]["verwaltungseinheit"];
            f[attr.wasserkoerper] = f["raeumlich"]["wasserkoerper"];
            delete f.raeumlich;
        }

        if (!f.pageInfo) {
            f.pageInfo = getDefaultPageInfo("messstellenId");
        }
        let pageInfoParams = toQueryParams(f.pageInfo);
        delete f.pageInfo;

        delete f.extendedSearchOpen;
        if (Object.keys(f).length === 0) {
            return pageInfoParams;
        }
        return "q=" + toBase64QueryParam(f) + "&" + pageInfoParams;
    }, [attr.behoerde, attr.messstellenstatus, attr.messstellentyp, attr.regionalstelle, attr.verwaltungseinheit, attr.wasserkoerper, attr.abwasserbeseitigungspflichtiger]);

    const initialQuery = useRef(getMessstelleSucheEndpoint(createQuery({...storedFilter, ...sharedFilter})));
    const [{data, loading}, refetchMessstellen] = useHttp(initialQuery.current, {manual: true});
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (data && data.length > 0) {
            setSelected((prevSelected) => prevSelected.filter((selId) => data.find((dItem) => dItem.id === selId)));
        }
    }, [data, setSelected]);

    const executeSearch = (storedFilterParam, sharedFilterParam) => {
        if (initialized) {
            refetchMessstellen(getMessstelleSucheEndpoint(createQuery({...storedFilterParam, ...sharedFilterParam}))).catch((e) =>
                handleApiException(e, "Error loading Betriebsarbeitsliste")
            );
        }
        setInitialized(true);
    };

    usePersonalisationAndPagination(sharedFilter, storedFilter, executeSearch);

    return (
        <>
            <MessstelleArbeitsListeSuche
                id={id}
                formFactory={formFactory}
                attr={attr}
                sharedFilter={sharedFilter}
                onErweiterteSucheOpenChanged={onErweiterteSucheOpenChanged}
                sharedFormFactory={sharedFormFactory}
                regionalstelleOptionen={regionalstelleOptionen}
                raeumlichesKriteriumOptionen={raeumlichesKriteriumOptionen}
                behoerdeOptionen={behoerdeOptionen}
                storedFilter={storedFilter}
                setStoredFilter={setStoredFilter}
                setFilter={setFilter}
                resetSharedFilter={resetSharedFilter}
                filter={filter}
                setSelected={setSelected}
                executeSearch={executeSearch}
                initialSearchParameters={initialSearchParameters}
            />
            <MessstelleArbeitsListeTable
                id={id}
                loading={loading}
                data={data}
                history={history}
                selected={selected}
                setSelected={setSelected}
                storedFilter={storedFilter}
                setStoredFilter={setStoredFilter}
                sharedFilter={sharedFilter}
                createQuery={createQuery}
            />
        </>
    );
}

export default MessstelleArbeitsListe;
